import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Row, Col, Card, Typography, Button, Tag, Avatar, Divider, Empty, Spin, message,
} from 'antd';
import {
  CreditCardOutlined, ClockCircleOutlined, CheckCircleOutlined,
  FileTextOutlined, PlusOutlined, ArrowRightOutlined,
  UserOutlined, CalendarOutlined, DollarOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import api from '../../api/axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const IMG = process.env.REACT_APP_URL;

const formatPrice = (p) => {
  if (!p && p !== 0) return '0';
  return Number(p).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const STATUS_MAP = {
  pending: { color: 'orange', label: 'Kutilmoqda' },
  confirmed: { color: 'blue', label: 'Tasdiqlangan' },
  in_progress: { color: 'geekblue', label: 'Jarayonda' },
  completed: { color: 'green', label: 'Bajarildi' },
  cancelled: { color: 'red', label: 'Bekor qilingan' },
  rejected: { color: 'volcano', label: 'Rad etilgan' },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ label, value, icon, bg, ic }) => (
  <Card style={{ background: bg, border: 'none', borderRadius: 14 }} styles={{ body: { padding: '18px 20px' } }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
        <div style={{ fontSize: 26, fontWeight: 700, marginTop: 6, color: 'var(--qk-text)' }}>{value}</div>
      </div>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--qk-card-bg)', boxShadow: '0 2px 6px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: ic, flexShrink: 0 }}>
        {icon}
      </div>
    </div>
  </Card>
);

const MiniOrderCard = ({ order }) => {
  const st = STATUS_MAP[order.status] || STATUS_MAP.pending;
  return (
    <div style={{ border: '1px solid var(--qk-border)', borderRadius: 12, padding: '12px 14px', marginBottom: 10, background: 'var(--qk-bg-soft)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text strong style={{ fontSize: 13 }}>#{order.id}</Text>
        <Tag color={st.color} style={{ fontSize: 10, margin: 0 }}>{st.label}</Tag>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Avatar size={24} icon={<UserOutlined />} src={order.client_avatar ? IMG + order.client_avatar : undefined} />
        <Text style={{ fontSize: 12 }}>{order.client_name || '—'}</Text>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          <CalendarOutlined /> {order.day}/{order.month}/{order.year}
          {order.start ? ` · ${String(order.start).slice(0, 5)}` : ''}
        </Text>
        <Text strong style={{ fontSize: 12, color: '#22c55e' }}>{formatPrice(order.summa)} so'm</Text>
      </div>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return 'Xayrli tong';
  if (h >= 12 && h < 17) return 'Xayrli kun';
  if (h >= 17 && h < 22) return 'Xayrli kech';
  return 'Xayrli tun';
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      api.get('/orders/my').then(r => setOrders(r.data?.data || [])),
      api.get('/tasks').then(r => setTasks(r.data?.data || [])),
    ]).then(results => {
      if (results.some(r => r.status === 'rejected')) {
        message.error("Ma'lumotlarni yuklashda xatolik yuz berdi");
      }
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const timeStr = now.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const dateStr = now.toLocaleDateString('uz-UZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const firstName = user?.fullname?.split(' ')[0] || user?.phone || 'Foydalanuvchi';

  // Stats
  const stats = useMemo(() => {
    const total = orders.reduce((s, o) => s + Number(o.summa || 0), 0);
    const pending = orders.filter(o => o.status === 'pending' || o.status === 'confirmed');
    const inProgress = orders.filter(o => o.status === 'in_progress');
    const completed = orders.filter(o => o.status === 'completed');
    return { total, pending, inProgress, completed, all: orders.length };
  }, [orders]);

  // Recent orders (last 5 pending/confirmed)
  const recentPending = useMemo(() => orders.filter(o => o.status === 'pending' || o.status === 'confirmed').slice(0, 5), [orders]);
  const recentCompleted = useMemo(() => orders.filter(o => o.status === 'completed').slice(0, 5), [orders]);

  // Upcoming tasks (next 7 days)
  const upcomingTasks = useMemo(() => {
    const today = dayjs().format('YYYY-MM-DD');
    const week = dayjs().add(7, 'day').format('YYYY-MM-DD');
    return tasks.filter(t => {
      const d = t.date ? dayjs(t.date).format('YYYY-MM-DD') : null;
      return d && d >= today && d <= week;
    }).slice(0, 6);
  }, [tasks]);

  if (loading && orders.length === 0 && tasks.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* ══════════════════ LEFT MAIN CONTENT ══════════════════ */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Greeting + Time */}
        <Card style={{ borderRadius: 14, marginBottom: 16 }} styles={{ body: { padding: '20px 24px' } }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>{getGreeting()}, {firstName}</Title>
              <Text type="secondary" style={{ fontSize: 13 }}>Mijozlaringizga eng yaxshi xizmatni ko'rsating!</Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 1, fontVariantNumeric: 'tabular-nums', color: 'var(--qk-text)', lineHeight: 1.2 }}>
                {timeStr}
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>{dateStr}</Text>
              <div style={{ marginTop: 10 }}>
                <Button type="primary" icon={<PlusOutlined />} size="large" style={{ borderRadius: 10 }} onClick={() => navigate('/provider/orders')}>
                  Buyurtmalarga o'tish
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Stat Cards */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} xl={6}>
            <StatCard label="Jami tushum" value={`${formatPrice(stats.total)} so'm`} icon={<CreditCardOutlined />} bg="rgba(102,126,234,0.14)" ic="#667eea" />
          </Col>
          <Col xs={12} xl={6}>
            <StatCard label="Kutilmoqda" value={stats.pending.length} icon={<ClockCircleOutlined />} bg="rgba(245,158,11,0.14)" ic="#f59e0b" />
          </Col>
          <Col xs={12} xl={6}>
            <StatCard label="Jarayonda" value={stats.inProgress.length} icon={<FileTextOutlined />} bg="rgba(59,130,246,0.14)" ic="#3b82f6" />
          </Col>
          <Col xs={12} xl={6}>
            <StatCard label="Bajarildi" value={stats.completed.length} icon={<CheckCircleOutlined />} bg="rgba(34,197,94,0.14)" ic="#22c55e" />
          </Col>
        </Row>

        {/* Order Columns */}
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Card title={<Text strong style={{ fontSize: 15 }}>Yangi buyurtmalar</Text>} style={{ borderRadius: 14 }} styles={{ body: { padding: '12px 16px 8px' } }}>
              {recentPending.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Yangi buyurtmalar yo'q" />
              ) : (
                recentPending.map(o => <MiniOrderCard key={o.id} order={o} />)
              )}
              <Button type="text" block style={{ color: '#667eea', fontWeight: 500, marginTop: 4 }} onClick={() => navigate('/provider/orders')}>
                Barcha buyurtmalar <ArrowRightOutlined />
              </Button>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title={<Text strong style={{ fontSize: 15 }}>Bajarilgan buyurtmalar</Text>} style={{ borderRadius: 14 }} styles={{ body: { padding: '12px 16px 8px' } }}>
              {recentCompleted.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Bajarilgan buyurtmalar yo'q" />
              ) : (
                recentCompleted.map(o => <MiniOrderCard key={o.id} order={o} />)
              )}
              <Button type="text" block style={{ color: '#667eea', fontWeight: 500, marginTop: 4 }} onClick={() => navigate('/provider/moliya')}>
                Moliyaviy hisobot <ArrowRightOutlined />
              </Button>
            </Card>
          </Col>
        </Row>
      </div>

      {/* ══════════════════ RIGHT SIDEBAR ══════════════════ */}
      <div style={{ width: 280, flexShrink: 0 }}>
        {/* Quick Stats */}
        <Card style={{ borderRadius: 14, marginBottom: 16, background: 'linear-gradient(135deg, #667eea, #764ba2)' }} styles={{ body: { padding: '20px' } }}>
          <DollarOutlined style={{ fontSize: 24, color: 'rgba(255,255,255,0.8)', marginBottom: 8 }} />
          <div style={{ color: 'white', fontSize: 12, opacity: 0.8 }}>Shu oydagi tushum</div>
          <div style={{ color: 'white', fontSize: 24, fontWeight: 700, marginTop: 4 }}>
            {formatPrice(orders.filter(o => o.month === (new Date().getMonth() + 1) && o.year === new Date().getFullYear()).reduce((s, o) => s + Number(o.summa || 0), 0))} so'm
          </div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 4 }}>
            {orders.filter(o => o.month === (new Date().getMonth() + 1) && o.year === new Date().getFullYear()).length} ta buyurtma
          </div>
        </Card>

        {/* Upcoming Tasks */}
        <Card style={{ borderRadius: 14 }} styles={{ body: { padding: '16px 18px' } }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <Text strong style={{ fontSize: 14 }}>Yaqin vazifalar</Text>
            <Button type="link" size="small" style={{ color: '#667eea', padding: 0 }} onClick={() => navigate('/provider/calendar')}>
              Kalendar <ArrowRightOutlined />
            </Button>
          </div>
          {upcomingTasks.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Vazifalar yo'q" style={{ margin: '12px 0' }} />
          ) : (
            upcomingTasks.map((t, i) => (
              <React.Fragment key={t.id}>
                <div style={{ padding: '8px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: t.color || '#667eea', flexShrink: 0 }} />
                    <Text style={{ fontSize: 13, fontWeight: 500 }}>{t.title}</Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 16 }}>
                    {t.date ? dayjs(t.date).format('DD MMM') : ''} {t.start_time ? `· ${String(t.start_time).slice(0, 5)}` : ''}
                  </Text>
                </div>
                {i < upcomingTasks.length - 1 && <Divider style={{ margin: 0 }} />}
              </React.Fragment>
            ))
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
